import { CommentWithUser } from '@/types/comments';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminPermissions } from '@/shared/hooks/useAdminPermissions';
import { useAdminDeleteComment } from '@/shared/hooks/useAdminDeleteComment';
import AdminDeleteCommentModal from '@/components/modals/AdminDeleteCommentModal';
import { useState } from 'react';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { Link } from 'react-router-dom';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { getInitials } from '@/utils/avatarUtils';
import { formatShortDistance } from '@/utils/relativeTime';

interface CommentItemProps {
  comment: CommentWithUser;
  className?: string;
  onDelete?: (commentId: string) => void;
}


export function CommentItem({ comment, className, onDelete }: CommentItemProps) {
  const { user } = useAuth();
  const { isAdmin } = useAdminPermissions();
  const isOwnComment = user?.id === comment.user_id;
  const [showAdminDeleteModal, setShowAdminDeleteModal] = useState(false);
  const adminDeleteComment = useAdminDeleteComment();

  const handleAdminDelete = async (reason: string) => {
    try {
      await adminDeleteComment.mutateAsync({
        commentId: comment.id,
        reason: reason
      });
      setShowAdminDeleteModal(false);
      if (onDelete) onDelete(comment.id);
    } catch (error) {
      console.error('Error deleting comment as admin:', error);
    }
  };

  return (
    <div className={cn("flex space-x-3 py-3", className)}>
      {/* User Avatar */}
      <Link to={`/profile/${comment.username}`} className="relative w-8 h-8 flex-shrink-0 group">
        <FramedAvatar
          src={comment.user_avatar || undefined}
          initials={getInitials(comment.user_name, 'A')}
          beltRank={comment.belt_rank || 'white'}
          size={32}
          frameSize={{ w: 32, h: 36 }}
          className=""
          username={comment.username}
        />
      </Link>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2 mb-1">
          <div className="flex items-center flex-1 min-w-0 gap-2">
            <Link to={`/profile/${comment.username}`} className="font-semibold text-white text-sm truncate max-w-[100px] group-hover:underline">
              {comment.user_name || 'Anonymous'}
            </Link>
            {comment.is_premium && <PremiumBadge size="xs" />}
            <span className="text-xs text-gray-400 truncate max-w-[80px]">
              • {formatShortDistance(new Date(comment.created_at))}
          </span>
          </div>
          {/* 3-dot menu for own comment or admin */}
          {(isOwnComment || isAdmin) && (
            <div className="ml-auto">
              <CustomDropdownMenu
                trigger={
                  <span className="ml-2 p-1 rounded-full focus:outline-none cursor-pointer transition-colors inline-flex">
                    <MoreHorizontal className="h-4 w-4 text-gray-400" />
                  </span>
                }
                items={[
                  ...(isOwnComment ? [{
                    label: "Delete comment",
                    icon: <Trash2 className="h-4 w-4 mr-2" />,
                    onClick: () => onDelete?.(comment.id),
                    className: "text-white"
                  }] : []),
                  ...(isAdmin && !isOwnComment ? [{
                    label: "Delete as Admin",
                    icon: <Trash2 className="h-4 w-4 mr-2" />,
                    onClick: () => setShowAdminDeleteModal(true),
                    className: "text-red-400"
                  }] : [])
                ]}
              />
            </div>
          )}
        </div>
        <p className="text-gray-100 text-sm leading-relaxed break-words">
          {comment.content}
        </p>
      </div>

      {/* Admin Delete Modal */}
      <AdminDeleteCommentModal
        isOpen={showAdminDeleteModal}
        onClose={() => setShowAdminDeleteModal(false)}
        onConfirm={handleAdminDelete}
        isLoading={adminDeleteComment.isPending}
      />
    </div>
  );
} 